import { useEffect, useRef, forwardRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// Define the type for the props
interface PortalProps {
  children: ReactNode; // This makes sure that the 'children' prop is recognized
  [key: string]: any; // For any other dynamic props
}

const Portal = forwardRef<HTMLDivElement, PortalProps>(({ children, ...props }, ref) => {
  const portalElement = useRef<HTMLDivElement | null>(null);

  // Created while rendering rather than in the effect below: a consumer reading the forwarded
  // ref does so in its own mount lifecycle, which runs after ours, so the portal has to have
  // rendered on the first pass for that ref to hold a node. The element is detached until the
  // effect attaches it, which keeps this side-effect free as far as the document is concerned.
  if (portalElement.current === null && typeof document !== 'undefined') {
    portalElement.current = document.createElement('div');
  }

  useEffect(() => {
    const p = portalElement.current;
    if (!p) return;

    document.body.appendChild(p);

    return () => {
      p.remove();
    };
  }, []);

  const duration = 200;
  const styles = `
    .fade-enter { opacity: 0.01; }
    .fade-enter.fade-enter-active { opacity: 1; transition: opacity ${duration}ms; }
    .fade-leave { opacity: 1; }
    .fade-leave.fade-leave-active { opacity: 0.01; transition: opacity ${duration}ms; }
  `;

  return portalElement.current
    ? createPortal(
        <div ref={ref}>
          <style>{styles}</style>
          <TransitionGroup {...props}>
            <CSSTransition timeout={{ enter: duration, exit: duration }} classNames="fade">
              {children}
            </CSSTransition>
          </TransitionGroup>
        </div>,
        portalElement.current,
      )
    : null;
});

export default Portal;
